/**
 * Persistence + expiration de la session de checkout adhésion /a/[slug].
 * Encapsule l'usage de useCheckoutSession pour réduire la taille du composant.
 */
type SaveData = {
  planQuantities: Record<string, number>;
  participants: unknown[];
  buyerInfo: unknown;
  includeFees: boolean;
  donateUs: boolean;
  promoApplied: unknown;
};

interface RestoreTargets {
  planQuantities: Ref<Record<string, number>>;
  participants: Ref<unknown[]>;
  buyerInfo: Ref<unknown>;
  includeFees: Ref<boolean>;
  donateUs: Ref<boolean>;
  promoApplied: Ref<unknown>;
}

export function useMembershipCheckoutSession(
  slug: Ref<string>,
  checkoutStep: Ref<number>,
  restoreTargets: RestoreTargets,
  getSnapshot: () => SaveData,
) {
  const checkoutSession = useCheckoutSession(`membership:${slug.value}`);
  const isRestoringSession = ref(true);
  const sessionExpiredModal = ref(false);

  function dismissSessionExpired() {
    sessionExpiredModal.value = false;
    checkoutStep.value = 1;
  }

  let expirationTimer: ReturnType<typeof setInterval> | null = null;
  if (import.meta.client) {
    onMounted(() => {
      expirationTimer = setInterval(() => {
        if (checkoutStep.value > 1 && checkoutSession.isExpired()) {
          checkoutSession.clear();
          sessionExpiredModal.value = true;
        }
      }, 5000);
    });
    onUnmounted(() => { if (expirationTimer) clearInterval(expirationTimer); });
  }

  function saveCheckoutState() {
    if (isRestoringSession.value) return;
    checkoutSession.syncStepToUrl(checkoutStep.value);
    if (checkoutStep.value <= 1) return;
    checkoutSession.save(checkoutStep.value, getSnapshot());
  }

  function restoreCheckoutState() {
    const saved = checkoutSession.restore();
    if (!saved) { isRestoringSession.value = false; return; }
    if (saved.expired) {
      isRestoringSession.value = false;
      sessionExpiredModal.value = true;
      return;
    }
    if (saved.step <= 1) { isRestoringSession.value = false; return; }
    const d = saved.data as Partial<SaveData>;
    if (d.planQuantities) restoreTargets.planQuantities.value = d.planQuantities;
    if (Array.isArray(d.participants) && d.participants.length) restoreTargets.participants.value = d.participants;
    if (d.buyerInfo) restoreTargets.buyerInfo.value = d.buyerInfo;
    if (d.includeFees !== undefined) restoreTargets.includeFees.value = d.includeFees;
    if (d.donateUs !== undefined) restoreTargets.donateUs.value = d.donateUs;
    if (d.promoApplied) restoreTargets.promoApplied.value = d.promoApplied;
    checkoutStep.value = Math.min(saved.step, 4);
    checkoutSession.syncStepToUrl(checkoutStep.value);
    nextTick(() => { isRestoringSession.value = false; });
  }

  function clearCheckoutSession() {
    checkoutSession.clear();
  }

  return {
    sessionExpiredModal,
    dismissSessionExpired,
    saveCheckoutState,
    restoreCheckoutState,
    clearCheckoutSession,
    isRestoringSession,
  };
}
